
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { ReportData } from './types';

// Function to generate and download PDF report
export const downloadPDF = (report: ReportData) => {
  if (!report.detailedData) {
    return "Error: Report does not contain detailed data";
  }
  
  const doc = new jsPDF();
  const data = report.detailedData;
  
  // Title
  doc.setFontSize(20);
  doc.text(data.title, 20, 20);
  
  // Overview section
  doc.setFontSize(16);
  doc.text("Overview", 20, 40);
  doc.setFontSize(10);
  
  let yPos = 50;
  Object.entries(data.overview).forEach(([key, value]) => {
    const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    doc.text(`${formattedKey}: ${value}`, 20, yPos);
    yPos += 7;
  });
  
  // Consumption or Cost Breakdown section
  yPos += 10;
  doc.setFontSize(16);
  doc.text("Breakdown", 20, yPos);
  yPos += 10;
  doc.setFontSize(10);
  
  // Create table for breakdown data
  if (data.consumptionBreakdown) {
    doc.text("Category", 20, yPos);
    doc.text("Usage", 80, yPos);
    doc.text("Cost", 120, yPos);
    doc.text("%", 160, yPos);
    yPos += 7;
    
    data.consumptionBreakdown.forEach(item => {
      doc.text(item.category, 20, yPos);
      doc.text(item.consumption, 80, yPos);
      doc.text(item.cost, 120, yPos);
      doc.text(`${item.percentage}%`, 160, yPos);
      yPos += 7;
    });
  } else if (data.monthlyBreakdown) {
    doc.text("Month", 20, yPos);
    doc.text("Usage (kWh)", 80, yPos);
    doc.text("Cost ($)", 120, yPos);
    doc.text("%", 160, yPos);
    yPos += 7;
    
    data.monthlyBreakdown.forEach(item => {
      doc.text(item.month, 20, yPos);
      doc.text(item.consumption.toString(), 80, yPos);
      doc.text(item.cost.toString(), 120, yPos);
      doc.text(`${item.percentageOfTotal}%`, 160, yPos);
      yPos += 7;
    });
  } else if (data.seasonalAnalysis) {
    doc.text("Season", 20, yPos);
    doc.text("Usage (kWh)", 80, yPos);
    doc.text("Cost ($)", 120, yPos);
    doc.text("%", 160, yPos);
    yPos += 7;
    
    data.seasonalAnalysis.forEach(item => {
      doc.text(item.season, 20, yPos);
      doc.text(item.consumption.toString(), 80, yPos);
      doc.text(item.cost.toString(), 120, yPos);
      doc.text(`${item.percentageOfTotal}%`, 160, yPos);
      yPos += 7;
    });
  }
  
  // Add recommendations if available
  if (data.recommendations) {
    yPos += 10;
    doc.setFontSize(16);
    doc.text("Recommendations", 20, yPos);
    yPos += 10;
    doc.setFontSize(10);
    
    data.recommendations.forEach(recommendation => {
      doc.text(`• ${recommendation}`, 20, yPos);
      yPos += 7;
    });
  }
  
  doc.save(`${report.title.replace(/\s+/g, '_')}.pdf`);
  
  return `${report.title} has been downloaded successfully.`;
};

// Function to generate and download Excel report
export const downloadExcel = (report: ReportData) => {
  if (!report.detailedData) {
    return "Error: Report does not contain detailed data";
  }
  
  const data = report.detailedData;
  const workbook = XLSX.utils.book_new();
  
  // Overview sheet
  const overviewData = [
    ["Overview"],
    ...Object.entries(data.overview).map(([key, value]) => {
      const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      return [formattedKey, value];
    })
  ];
  const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
  XLSX.utils.book_append_sheet(workbook, overviewSheet, "Overview");
  
  // Breakdown sheet
  let breakdownSheet;
  if (data.consumptionBreakdown) {
    const breakdownData = [
      ["Category", "Consumption", "Cost", "Percentage"],
      ...data.consumptionBreakdown.map(item => [
        item.category, 
        item.consumption, 
        item.cost, 
        `${item.percentage}%`
      ])
    ];
    breakdownSheet = XLSX.utils.aoa_to_sheet(breakdownData);
  } else if (data.monthlyBreakdown) {
    const breakdownData = [
      ["Month", "Consumption (kWh)", "Cost ($)", "Percentage of Total"],
      ...data.monthlyBreakdown.map(item => [
        item.month, 
        item.consumption, 
        item.cost, 
        `${item.percentageOfTotal}%`
      ])
    ];
    breakdownSheet = XLSX.utils.aoa_to_sheet(breakdownData);
  } else if (data.seasonalAnalysis) {
    const breakdownData = [
      ["Season", "Consumption (kWh)", "Cost ($)", "Percentage of Total"],
      ...data.seasonalAnalysis.map(item => [
        item.season, 
        item.consumption, 
        item.cost, 
        `${item.percentageOfTotal}%`
      ])
    ];
    breakdownSheet = XLSX.utils.aoa_to_sheet(breakdownData);
  }
  
  XLSX.utils.book_append_sheet(workbook, breakdownSheet, "Breakdown");
  
  // Add additional sheet based on report type
  if (data.dailyReadings) {
    const readingsData = [
      ["Date", "Consumption (kWh)", "Cost ($)"],
      ...data.dailyReadings.map(item => [
        item.date,
        item.consumption,
        item.cost
      ])
    ];
    const readingsSheet = XLSX.utils.aoa_to_sheet(readingsData);
    XLSX.utils.book_append_sheet(workbook, readingsSheet, "Daily Readings");
  } else if (data.facilityComparison) {
    const facilityData = [
      ["Facility", "Consumption (kWh)", "Cost ($)", "Efficiency"],
      ...data.facilityComparison.map(item => [
        item.facility,
        item.consumption,
        item.cost,
        item.efficiency
      ])
    ];
    const facilitySheet = XLSX.utils.aoa_to_sheet(facilityData);
    XLSX.utils.book_append_sheet(workbook, facilitySheet, "Facility Comparison");
  } else if (data.yearOverYearComparison) {
    const yearlyData = [
      ["Year", "Consumption (kWh)", "Cost ($)"],
      ...data.yearOverYearComparison.map(item => [
        item.year,
        item.consumption,
        item.cost
      ])
    ];
    const yearlySheet = XLSX.utils.aoa_to_sheet(yearlyData);
    XLSX.utils.book_append_sheet(workbook, yearlySheet, "Yearly Comparison");
  }
  
  // Generate Excel file
  XLSX.writeFile(workbook, `${report.title.replace(/\s+/g, '_')}.xlsx`);
  
  return `${report.title} has been downloaded successfully.`;
};
